import * as c2pService from '@api/company-person';
import { c3SearchDataType } from '../../add/types';
import * as pintService from '@api/pint';
import { ColoredButton, ColoredChip, ColoredChipIconButton } from '@components/colored-elements';
import { IngestionMethodChip } from '@components/ingestion-method';
import { RelationSourceChip } from '@components/relation-source-chip';
import { AiOutlinePlusCircle, BiEdit, MdDelete } from '@icon/react-icons';
import { rbac, useToggle } from '@lib/common';
import { createReturnParam } from '@lib/deals';
import { Box, Button, Chip, Dialog, DialogActions, DialogTitle, TextField, Tooltip, Typography } from '@material-ui/core';
import { blue, green, red } from '@material-ui/core/colors';
import CopyClipboard from '@material-ui/icons/AttachFile';
import { useSnackbar } from 'notistack';
import { CellForLastModifiedAt } from 'pages/company/search/columns/utils';
import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { CellProps, Column } from 'react-table';
import styled from 'styled-components';
import DeleteIcon from '@material-ui/icons/DeleteRounded';


import {
  $eq,
  DealFilter,
  StringFilters,
  withEnumMultiSelect,
  withMultiSelectAutoComplete,
} from '../filters';
import { $contains, $in } from '../filters/operators';
import { useRefreshCounter } from "../c3-search";
import ClipBoard, {
  AddNonLinkedEntityFilter,
  addTitleToFilter,
  CellForContentLanguages,
  CellForReleaseYear,
  createEnumMap,
  fetchRoles,
  fetchSubRoles,
  SingleBuyerSellerCell,
} from './utils';
import { deleteC3Relation } from '@api/c3/c3.api';
import { withRangeFilters } from '@pages/content/search/filters';
import { ContentStatus } from '@lib/content';
import { Autocomplete } from '@material-ui/lab';
import { CellForContentSource } from '@pages/content/search/columns/utils';

type GlobalFilterColumn<D extends object = Record<string, any>> = Column<D> & {
  /**
   * Setting this to true will make the column come up in primary filters
   */
  primaryFilter?: boolean;
  /**
   * The operator to use with the primary filter. Use with `primaryFilter`
   */
  primaryFilterOperator?: string;
  /**
   * Force the columns to hide, this is usefull when you want to add a column
   * only to add the filter.
   */
  forceHide?: boolean;
  /**
   * A passive column is a column that can be used to filter, but
   * shows up in advanced filter.
   */
  passiveColumn?: boolean;
  overrideOperator?: { [k: string]: Partial<DealFilter> };
};

const TitledStringFilter = addTitleToFilter(StringFilters);
const EntityFilters = addTitleToFilter(AddNonLinkedEntityFilter(StringFilters));

const TitledNumericRangeFilter = addTitleToFilter(
  withRangeFilters(1900, 2100, 2010, 2020),
  null,
  'Filter C3 by Year Range'
);

export const CellForCompEntity = () => (props: PropsWithChildren<CellProps<c3SearchDataType>>) => {
  const associated_comp: any = props?.row?.original;
  const RenderComponent = SingleBuyerSellerCell;

  if (!associated_comp?.company_name && !associated_comp?.company_vtr_id) {
    return null;
  }

  const firstEntityName = associated_comp?.company_name;
  return (
    <>
      <RenderComponent name={<>{firstEntityName} </>} vtrId={associated_comp?.company_vtr_id} />
    </>
  );
};


export const columns: GlobalFilterColumn<c3SearchDataType>[] = [
  {
    id: 'vtr_id',
    primaryFilter: true,
    primaryFilterOperator: $eq.key,
    Header: 'Vitrina Id',
    forceHide: true,
    disableFilters: true,
  },
  {
    id: 'content_title',
    accessor: row => row.content_title || 'NA',
    primaryFilter: true,
    primaryFilterOperator: $contains.key,
    Header: 'Content Title',
    disableFilters: true,
    Filter: EntityFilters,
    Cell: ({ row }) => {
      const { content_vtr_id = '', content_title = '' } = row?.original;
      return (
        <Box>
          {content_vtr_id && (
            <>
              <ClipBoard
                parentValue={
                  <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                    {row?.original?.content_title}
                  </Typography>
                }
                icon={<CopyClipboard style={{ fontSize: '1.2em' }} color="primary" />}
                value={content_vtr_id}
              />
              <br />
              <Typography variant="caption" color="textSecondary">
                {row?.original?.content_type} • {row?.original?.language} • {row?.original?.genres}
              </Typography>
            </>
          )}
        </Box>
      );
    },
  },
  {
    id: 'content_status',
    Header: 'Content Status',
    accessor: row => row.content_status || 'NA',
    Cell: ({ value }: { value: string }) => (
      <Chip
        label={value}
        size="small"
        color={
          value === 'Released'
            ? 'primary'
            : value === 'In Production'
              ? 'secondary'
              : value === 'Post Production'
                ? 'default'
                : 'default'
        }
        variant="outlined"
      />
    ),
    width: 120,
    Filter: addTitleToFilter(
      withEnumMultiSelect(createEnumMap(ContentStatus)),
      null,
      'Filter C3 by Content status',
    ),
    overrideOperator: {
      [$in.key]: {
        ChipLabel: ({ label, value }) => (
          <>{`${label} one of ${value}
                `}</>
        ),
      },
    },
    passiveColumn: true,
    forceHide: false,
  },
  {
    id: 'person_name',
    accessor: row => row.person_name || 'NA',
    primaryFilter: true,
    primaryFilterOperator: $contains.key,
    Header: 'Person Name',
    disableFilters: true,
    Filter: EntityFilters,
    Cell: ({ row }) => {
      const { person_vtr_id = '', person_name = '' } = row?.original;
      return (
        <ClipBoard
          parentValue={person_name}
          icon={<CopyClipboard style={{ fontSize: '1.2em' }} color="primary" />}
          value={person_vtr_id ?? 'No Person VTR ID associated'}
        />
      );
    },
  },
  {
    id: 'imdb_id',
    primaryFilter: true,
    primaryFilterOperator: $contains.key,
    Header: 'IMDB Id',
    forceHide: true,
  },
  {
    id: 'tmdb_id',
    primaryFilter: true,
    primaryFilterOperator: $contains.key,
    Header: 'TMDB Id',
    forceHide: true,
    disableFilters: true,
  },
  {
    id: 'origin_country',
    primaryFilter: false,
    primaryFilterOperator: $contains.key,
    Header: 'Country of Origin',
    // Cell: CellForHQCountry,
    Filter: addTitleToFilter(
      withMultiSelectAutoComplete(pintService.getPintMasters, 'countries', 'id'),
      null,
      'Filter Contents by Country of Origin',
    ),
    passiveColumn: true,
    forceHide: true,
  },
  {
    id: 'character',
    accessor: row => row?.character || 'NA',
    primaryFilter: false,
    // primaryFilterOperator: $contains.key,
    Header: 'Character',
    // Filter: TitledStringFilter,
    // minWidth: 150,
    // passiveColumn: true,
    disableFilters: true,
    forceHide: false,
    disableSortBy: true,
  },
  {
    id: 'role',
    accessor: row => row.role || 'NA',
    primaryFilter: false,
    primaryFilterOperator: $contains.key,
    Header: 'Role',
    Filter: TitledStringFilter,
    // minWidth: 150,
    //   passiveColumn: true,
    forceHide: false,
    disableSortBy: true,
  },
  {
    id: 'sub_role',
    accessor: row => row.sub_role || 'NA',
    primaryFilter: false,
    // primaryFilterOperator: $contains.key,
    Header: 'Sub Role',
    Filter: TitledStringFilter,
    // passiveColumn: true,
    forceHide: false,
    // minWidth: 150,
    // disableSortBy: true,
  },
  {
    id: 'language',
    // primaryFilter: true,
    // primaryFilterOperator: $contains.key,
    Header: 'Languages',
    Cell: CellForContentLanguages,
    Filter: addTitleToFilter(
      withMultiSelectAutoComplete(pintService.getPintMasters, 'languages', 'id'),
      null,
      'Filter Content by Languages',
    ),
    // disableFilters: true,
    passiveColumn: true,
    forceHide: true,
    disableSortBy: true,
    // primaryFilterOrder: 1,
  },
  {
    id: 'release_year',
    Header: 'Release Year',
    accessor: row => row.release_year || 'NA',
    width: 100,
    Cell: CellForReleaseYear,
    Filter: TitledNumericRangeFilter,
  },

  {
    id: 'source',
    // accessor: row => row.source || 'NA',
    primaryFilter: false,
    //primaryFilterOperator: $eq.key,
    Header: 'Source',
    Filter: EntityFilters,
    // minWidth: 150,
    disableSortBy: true,
    Cell: CellForContentSource /* ({ row }) => {
  const source = row.original.source; // 👈 comes from your dataset
  return (
    <Chip
      label={source || 'NA'}
      size="small"
      color={
        source === 'IMDB'
          ? 'primary'
          : source === 'Manual Entry'
          ? 'secondary'
          : source === 'TMDB'
          ? 'default'
          : 'default'
      }
      variant="default"
    />
  );
}, */,

    /*  Filter: addTitleToFilter(
      withEnumMultiSelect(createEnumMap(C2PRelationSourceType, true), true, true),
      null,
      'Filter Companies by Source'
    ),
    overrideOperator: {
      [$eq.key]: {
        ChipLabel: ({ label, value }) => (
          <>{`${label} one of ${value}
            `}</>
        ),
      },
    }, */
  },
  {
    id: 'ingestion_method',
    //  accessor: row => row.ingestion_method || 'NA',
    primaryFilter: false,
    primaryFilterOperator: $eq.key,
    Header: 'Ingestion method',
    // Filter: EntityFilters,
    // minWidth: 150,
    disableSortBy: true,
    forceHide: true,
    Cell: ({ row }) => {
      const ingestion_method = row.original.ingestion_method; // 👈 comes from your dataset
      return (
        <Chip
          label={ingestion_method || 'NA'}
          size="small"
          color={
            ingestion_method === 'API'
              ? 'primary'
              : ingestion_method === 'Manual'
                ? 'secondary'
                : 'default'
          }
          variant="default"
        />
      );
    },
    /* Filter: addTitleToFilter(
      withEnumMultiSelect(createEnumMap(C2PIngestionMethod, true), true, true),
      null,
      'Filter Companies by Ingestion Method'
    ),
    overrideOperator: {
      [$eq.key]: {
        ChipLabel: ({ label, value }) => (
          <>{`${label} one of ${value}
            `}</>
        ),
      },
    }, */
  },
  /*  {
    id: 'modified_at',
    accessor: row => row?.modified_at || 'NA',
    primaryFilter: false,
    primaryFilterOperator: $contains.key,
    Header: 'Modified at',
    Cell: CellForLastModifiedAt,
    Filter: EntityFilters,
    // minWidth: 150,
    disableFilters: true,
  }, */
  {
    id: 'actions',
    minWidth: 100,
    // maxWidth: 80,
    Header: 'Actions',
    Cell({ row }) {
      const returnTo = createReturnParam('/c3-page/search');
      const { vtr_id = '' } = row?.original;
      const disableTheDeleteBtn = vtr_id;
      return (
        <>
          <IconContainer>
            <Tooltip title="Edit C3 Relation">
              <ColoredChipIconButton
                component={Link}
                to={`/c3-page/edit/${vtr_id}${returnTo}`}
                pointer
                color="primary"
                size="medium"
                icon={<BiEdit />}
              />
            </Tooltip>
            <Tooltip title="Delete C3 Relation">
              <DeleteC3RelationDialog
                contentPersonRelationId={vtr_id}
                deleteDisable={
                  false
                } /* {disableTheDeleteBtn ? true : rbac('C3_Delete', '', 'Button')} */
                deleteC3
              />
            </Tooltip>
          </IconContainer>
        </>
      );
    },
  },
];

export const DeleteC3RelationDialog = React.forwardRef(
  (
    {
      buttonType = 'Icon',
      contentPersonRelationId,
      deleteDisable,
      deleteC3 = true,
      isButton,
      ...props
    }: any,
    ref
  ) => {
   const { forceRefresh } = useRefreshCounter();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [editOpen, , showEditDialog, hideEditDialog] = useToggle(false);
  const history = useHistory();
    const onDeleteFinish = useCallback(RelationId => {
      
      
      const infoNotification = enqueueSnackbar('Please wait deleting C3', {
        variant: 'info',
      });
      return deleteC3Relation(RelationId)
  .subscribe({
          next: () => {
            enqueueSnackbar('C3 deleted Successfully!!', { variant: 'success' });
             hideEditDialog();
            forceRefresh();
           // history.push('/c3-page/search');
          },
          error: err => enqueueSnackbar(`Error deleting C3 relation (${err?.response?.data?.error})`, { variant: 'error' }),
          complete: () => closeSnackbar(infoNotification),
        })
    }, []);

    return (
      <div ref={ref as React.RefObject<HTMLDivElement>}>
        {buttonType === 'Icon' ? (
          deleteC3 ? ( isButton ? <ColoredButton
          colored={blue[700]}
          coloredHover={blue[900]}
          startIcon={<DeleteIcon />}
          variant="contained"
          size="small"
/*           disabled={disableDeleteBtn ? true : rbac('C2P_Delete', '', 'Button')}
 */          onClick={showEditDialog}>
          Delete
        </ColoredButton> :(
            <ColoredChipIconButton
              pointer
              onClick={showEditDialog}
              colored={red[700]}
              coloredHover={red[900]}
              size="medium"
              icon={<MdDelete />}
              disabled={deleteDisable}
              {...props} //Extra props are sent from tooltip to children
            />
          )) : (
            <ColoredChipIconButton
              pointer
              onClick={showEditDialog}
              // colored={blue[700]}
              // coloredHover={blue[900]}
              size="small"
              color="primary"
              icon={<BiEdit />}
              {...props} //Extra props are sent from tooltip to children
            />
          )
        ) : (
          <ColoredButton
            colored={green[700]}
            coloredHover={green[900]}
            startIcon={<AiOutlinePlusCircle />}
            component="button"
            onClick={showEditDialog}
            variant="contained">
            Add
          </ColoredButton>
        )}
        <C3DeleteDialog
          contentPersonRelationId={contentPersonRelationId}
          onDeleteFinish={onDeleteFinish}
          deleteC3
          open={editOpen}
          onClose={hideEditDialog}
        />
      </div>
    );
  }
);

export function C3DeleteDialog({
  contentPersonRelationId,
  deleteC3 = false,
  onDeleteFinish = null,
  onClose = null,
  open,
}) {
  return deleteC3 ? (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description">
      <DialogTitle id="alert-dialog-title">
        Are you sure you want to delete this C3 Relation?
      </DialogTitle>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button onClick={() => onDeleteFinish(contentPersonRelationId)} color="primary" autoFocus>
          Ok
        </Button>
      </DialogActions>
    </Dialog>
  ) : null;
}
const IconContainer = styled.span`
  display: flex;
  gap: 8px;
  padding: 15px 10px;
  justify-content: center;
`;

export function RoleMultiSelectFilter({ column: { filterValue = [], setFilter } }) {
  const [roleOptions, setRoleOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadRoles = async () => {
      setLoading(true);
      const roles = await fetchRoles();
      setRoleOptions(roles || []);
      setLoading(false);
    };
    loadRoles();
  }, []);
  return (
    <Autocomplete style={{width: '200px'}}
      multiple
       loading={loading}
      options={roleOptions}
      value={filterValue || []}
      onChange={(_, newValue) => {
        setFilter(newValue.length ? newValue : undefined)}}
      renderInput={(params) => (
        <TextField {...params} variant="outlined" label="Multiple Select" size="small" />
      )}
    />
  );
}

export function SubRoleMultiSelectFilter({ column: { filterValue = [], setFilter } }) {
   const [subRoleOptions, setSubRoleOptions] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadSubRoles = async () => {
      setLoading(true);
      const data = await fetchSubRoles();
      setSubRoleOptions(data || {});
      setLoading(false);
    };
    loadSubRoles();
  }, []);

   // Flatten the subrole options into array of objects
  const allSubRoles = Object.entries(subRoleOptions).flatMap(([role, subroles]) =>
    subroles.map((sr) => ({
      id: `${role}-${sr}`,
      role,
      subrole: sr
    }))
  );

  // We must ensure the value is one of these objects, not just strings.
  const selectedValues = allSubRoles.filter((opt) =>
    filterValue.some((v) => v.subrole === opt.subrole && v.role === opt.role)
  );

  return (
    <Autocomplete style={{width: '200px'}}
      multiple
        loading={loading}
      options={allSubRoles}
      groupBy={(option) => option.role} // 👈 groups by Role
      getOptionLabel={(option) => option.subrole}
       value={selectedValues}
      onChange={(_, newValue) => {
        // Store the array of selected objects (role + subrole)
        setFilter(newValue.length ? newValue : undefined);
      }}
      renderInput={(params) => (
        <TextField {...params} variant="outlined" label="Select SubRoles" size="small" />
      )}
    />
  );
}